import React from 'react'
import clsx from 'clsx'
import PlainModal, { PlainModalSize } from './PlainModal'
import styles from './ConfirmDialog.module.scss'

interface DialogAction {
  label: string;
  onClick: () => void;
  variant?: 'text' | 'outlined' | 'contained';
  disabled?: boolean;
}

interface ConfirmDialogProps {
  open: boolean;
  title?: React.ReactNode;
  content?: React.ReactNode;
  actions?: DialogAction[];
  onClose: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  dataTestId?: string;
  contentDesc?: string;
  ariaLabel?: string;
  /** When true, backdrop click does not close (required flows e.g. T2-6634). */
  disableBackdropClose?: boolean;
  disableEscapeKeyDown?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  content,
  actions,
  onClose,
  maxWidth = 'xs',
  dataTestId = 'confirm-dialog',
  contentDesc,
  ariaLabel,
  disableBackdropClose = false,
  disableEscapeKeyDown = false,
}) => {
  const titleText = typeof title === 'string' ? title : undefined;
  const modalLabel = ariaLabel || titleText || 'Confirmation dialog';

  return (
    <PlainModal
      isOpen={open}
      onClose={onClose}
      ariaLabel={modalLabel}
      contentDesc={contentDesc || modalLabel}
      dataTestId={dataTestId}
      size={maxWidth as PlainModalSize}
      dialogClassName={styles.dialog}
      disableBackdropClose={disableBackdropClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      {title ? <h2 className={styles.title}>{title}</h2> : null}
      {content ? <div className={styles.content}>{content}</div> : null}
      {actions && actions.length > 0 ? (
        <div className={styles.actions}>
          {actions.map((a, idx) => {
            const locatorProps = {
              'content-desc': a.label,
            } as React.ButtonHTMLAttributes<HTMLButtonElement> & { 'content-desc'?: string };

            return (
              <button
                key={idx}
                type="button"
                className={clsx(styles.actionButton, {
                  [styles.contained]: (a.variant || 'text') === 'contained',
                  [styles.outlined]: (a.variant || 'text') === 'outlined',
                  [styles.text]: (a.variant || 'text') === 'text',
                })}
                onClick={a.onClick}
                disabled={a.disabled}
                aria-label={a.label}
                data-testid={`confirm-dialog-action-${idx}`}
                {...locatorProps}
              >
                {a.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </PlainModal>
  )
}

export default ConfirmDialog


